/**
 * QMS Use Case
 * Business logic layer for Quality & Risk Management
 * Implements Clean Architecture use case pattern
 */

import type { IQMSRepository } from "@/repositories/QMSRepository";
import { assertESignatureCeremonyPayload } from "@/utils/buildAttestationESignature";
import type { ESignatureCeremonyPayload } from "@/types/eSignature.type";
import {
  toQmsPriority,
  type QMSRecord,
  type CreateQMSRecordRequest,
  type UpdateQMSRecordRequest,
  type QMSListParams,
  type QMSListResponse,
  type QMSDashboardData,
  type QMSComment,
  type CAPARecord,
} from "@/types/qms.type";
import {
  applyQmsDisplayNames,
  applyQmsDisplayNamesToList,
  type QmsDisplayLookups,
  type QmsSiteLookup,
  type QmsStudyLookup,
  type QmsUserLookup,
} from "@/utils/qms/qmsRecordEnrichment";

/** Optional resolvers so QMS can show study/site/owner names instead of UUIDs. */
export interface QmsEnrichmentDeps {
  getStudyById: (studyId: string) => Promise<QmsStudyLookup>;
  getSitesByStudy: (studyId: string) => Promise<QmsSiteLookup[]>;
  getUsers: () => Promise<QmsUserLookup[]>;
}

/** Matches NestJS MinLength(10) on CAPA text fields. */
const CAPA_TEXT_MIN_LENGTH = 10;

const deriveCapaOwnerInitials = (capaOwner: string): string => {
  const parts = capaOwner.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "";
  }
  if (parts.length === 1) {
    return (parts[0] ?? "").slice(0, 2).toUpperCase();
  }
  return parts
    .map((p) => p[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 10);
};

/** Convert date-only or ISO strings to API timestamps. */
const toCapaApiDate = (value: string): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    return "";
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${trimmed}T23:59:59.000Z`;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return trimmed;
  }
  return parsed.toISOString();
};

const parseCapaDate = (value: string): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return new Date(value);
};

const normalizeCapa = (capa: CAPARecord): CAPARecord => {
  const capaOwner = capa.capaOwner?.trim() ?? "";
  const initials =
    capa.capaOwnerInitials?.trim() || deriveCapaOwnerInitials(capaOwner);
  const effectivenessCheckDate = toCapaApiDate(
    capa.effectivenessCheckDate ?? "",
  );

  return {
    correctiveAction: capa.correctiveAction?.trim() ?? "",
    preventiveAction: capa.preventiveAction?.trim() ?? "",
    capaOwner,
    capaOwnerInitials: initials.slice(0, 10),
    capaDueDate: toCapaApiDate(capa.capaDueDate ?? ""),
    effectivenessCheck: capa.effectivenessCheck?.trim() ?? "",
    effectivenessCheckDate,
    effectivenessApproved: capa.effectivenessApproved ?? false,
  };
};

const assertCapaTextMinLength = (label: string, value: string): void => {
  if (value.length < CAPA_TEXT_MIN_LENGTH) {
    throw new Error(
      `${label} must be at least ${CAPA_TEXT_MIN_LENGTH} characters`,
    );
  }
};

const validateCapaForSave = (
  capa: CAPARecord,
  requireDueDate: boolean,
): void => {
  if (!capa.correctiveAction) {
    throw new Error("Corrective action is required");
  }
  assertCapaTextMinLength("Corrective action", capa.correctiveAction);

  if (!capa.preventiveAction) {
    throw new Error("Preventive action is required");
  }
  assertCapaTextMinLength("Preventive action", capa.preventiveAction);

  if (!capa.capaOwner) {
    throw new Error("CAPA owner is required");
  }
  if (!capa.capaOwnerInitials) {
    throw new Error("CAPA owner initials are required");
  }

  if (!capa.effectivenessCheck) {
    throw new Error("Effectiveness check criteria is required");
  }
  assertCapaTextMinLength("Effectiveness check", capa.effectivenessCheck);

  if (requireDueDate) {
    if (!capa.capaDueDate) {
      throw new Error("CAPA due date is required");
    }
    const dueDate = parseCapaDate(capa.capaDueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dueDate < today) {
      throw new Error("CAPA due date must be in the future");
    }
  }
};

// ─── Use Case Interface ──────────────────────────────────────────────────────

export interface IQMSUseCase {
  // Dashboard
  getDashboard(studyId: string): Promise<QMSDashboardData>;

  // List & Filter
  getRecords(studyId: string, params?: QMSListParams): Promise<QMSListResponse>;

  // Single Record
  getRecordById(studyId: string, recordId: string): Promise<QMSRecord>;

  // Create, Update, Delete
  createRecord(
    studyId: string,
    data: CreateQMSRecordRequest,
  ): Promise<QMSRecord>;

  updateRecord(
    studyId: string,
    recordId: string,
    data: UpdateQMSRecordRequest,
  ): Promise<QMSRecord>;

  deleteRecord(studyId: string, recordId: string): Promise<void>;

  // Status Management
  closeRecord(studyId: string, recordId: string, note?: string): Promise<void>;

  reopenRecord(studyId: string, recordId: string, note?: string): Promise<void>;

  // Comments
  addComment(
    studyId: string,
    recordId: string,
    text: string,
  ): Promise<QMSComment>;
  listComments(studyId: string, recordId: string): Promise<QMSComment[]>;
  updateComment(
    studyId: string,
    recordId: string,
    commentId: string,
    text: string,
  ): Promise<QMSComment>;
  deleteComment(
    studyId: string,
    recordId: string,
    commentId: string,
  ): Promise<void>;

  getComments(studyId: string, recordId: string): Promise<QMSComment[]>;

  // CAPA Management
  addCapa(
    studyId: string,
    recordId: string,
    capa: CAPARecord,
  ): Promise<QMSRecord>;

  updateCapa(
    studyId: string,
    recordId: string,
    capa: CAPARecord,
  ): Promise<QMSRecord>;

  approveCapa(
    studyId: string,
    recordId: string,
    payload: {
      effectivenessNotes?: string;
      eSignature: ESignatureCeremonyPayload;
    },
  ): Promise<QMSRecord>;
}

// ─── Use Case Implementation ─────────────────────────────────────────────────

export class QMSUseCase implements IQMSUseCase {
  private readonly lookupsByStudy = new Map<
    string,
    Promise<QmsDisplayLookups>
  >();

  constructor(
    private readonly qmsRepository: IQMSRepository,
    private readonly enrichment?: QmsEnrichmentDeps,
  ) {}

  private async loadDisplayLookups(
    studyId: string,
  ): Promise<QmsDisplayLookups> {
    if (!this.enrichment) {
      return {
        study: null,
        sitesById: new Map(),
        usersById: new Map(),
      };
    }

    const cached = this.lookupsByStudy.get(studyId);
    if (cached) {
      return cached;
    }

    const enrichment = this.enrichment;
    const pending = (async (): Promise<QmsDisplayLookups> => {
      const [studyResult, sitesResult, usersResult] = await Promise.allSettled([
        enrichment.getStudyById(studyId),
        enrichment.getSitesByStudy(studyId),
        enrichment.getUsers(),
      ]);

      if (
        studyResult.status === "rejected" &&
        sitesResult.status === "rejected" &&
        usersResult.status === "rejected"
      ) {
        this.lookupsByStudy.delete(studyId);
      }

      const sitesById = new Map<string, QmsSiteLookup>();
      if (sitesResult.status === "fulfilled") {
        for (const site of sitesResult.value) {
          if (site?.id) {
            sitesById.set(site.id, site);
          }
        }
      }

      const usersById = new Map<string, QmsUserLookup>();
      if (usersResult.status === "fulfilled") {
        for (const user of usersResult.value) {
          if (user?.id) {
            usersById.set(user.id, user);
          }
        }
      }

      return {
        study: studyResult.status === "fulfilled" ? studyResult.value : null,
        sitesById,
        usersById,
      };
    })();

    this.lookupsByStudy.set(studyId, pending);
    return pending;
  }

  private async enrichRecord(
    studyId: string,
    record: QMSRecord,
  ): Promise<QMSRecord> {
    if (!this.enrichment) {
      return record;
    }
    const lookups = await this.loadDisplayLookups(studyId);
    return applyQmsDisplayNames(record, lookups);
  }

  private async enrichRecords(
    studyId: string,
    records: QMSRecord[],
  ): Promise<QMSRecord[]> {
    if (!this.enrichment || records.length === 0) {
      return records;
    }
    const lookups = await this.loadDisplayLookups(studyId);
    return applyQmsDisplayNamesToList(records, lookups);
  }

  /**
   * Get dashboard metrics for study
   * Validates studyId before fetching data
   */
  async getDashboard(studyId: string): Promise<QMSDashboardData> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    const dashboard = await this.qmsRepository.getDashboard(studyId);
    if (!dashboard.recentRecords?.length) {
      return dashboard;
    }
    return {
      ...dashboard,
      recentRecords: await this.enrichRecords(studyId, dashboard.recentRecords),
    };
  }

  /**
   * Get paginated list of QMS records with filters
   * Applies default pagination if not provided
   */
  async getRecords(
    studyId: string,
    params?: QMSListParams,
  ): Promise<QMSListResponse> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }

    // Apply defaults
    const queryParams: QMSListParams = {
      page: params?.page ?? 1,
      limit: params?.limit ?? 25,
      ...params,
    };

    const response = await this.qmsRepository.getQMSRecords(
      studyId,
      queryParams,
    );
    return {
      ...response,
      items: await this.enrichRecords(studyId, response.items),
    };
  }

  /**
   * Get single QMS record by ID
   */
  async getRecordById(studyId: string, recordId: string): Promise<QMSRecord> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }
    const record = await this.qmsRepository.getQMSRecordById(studyId, recordId);
    return this.enrichRecord(studyId, record);
  }

  /**
   * Create new QMS record
   * Validates required fields before creation
   */
  async createRecord(
    studyId: string,
    data: CreateQMSRecordRequest,
  ): Promise<QMSRecord> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }

    // Validate required fields
    if (!data.type) {
      throw new Error("Record type is required");
    }
    if (!data.title?.trim()) {
      throw new Error("Title is required");
    }
    if (!data.description?.trim()) {
      throw new Error("Description is required");
    }
    if (!data.studyId) {
      throw new Error("Study ID is required");
    }
    if (!data.severity) {
      throw new Error("Severity is required");
    }
    if (!data.ownerId) {
      throw new Error("Owner is required");
    }
    if (!data.dueDate) {
      throw new Error("Due date is required");
    }

    // Validate due date is in the future
    const dueDate = new Date(data.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dueDate < today) {
      throw new Error("Due date must be in the future");
    }

    // API requires priority (Low|Medium|High); derive from severity when omitted.
    const payload: CreateQMSRecordRequest = {
      ...data,
      priority: data.priority ?? toQmsPriority(data.severity),
    };

    return this.enrichRecord(
      studyId,
      await this.qmsRepository.createQMSRecord(studyId, payload),
    );
  }

  /**
   * Update existing QMS record
   * Validates update data before applying changes
   */
  async updateRecord(
    studyId: string,
    recordId: string,
    data: UpdateQMSRecordRequest,
  ): Promise<QMSRecord> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }

    // Validate optional fields if provided
    if (data.title !== undefined && !data.title.trim()) {
      throw new Error("Title cannot be empty");
    }
    if (data.description !== undefined && !data.description.trim()) {
      throw new Error("Description cannot be empty");
    }

    // Validate due date if being updated
    if (data.dueDate) {
      const dueDate = new Date(data.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (dueDate < today) {
        throw new Error("Due date must be in the future");
      }
    }

    return this.enrichRecord(
      studyId,
      await this.qmsRepository.updateQMSRecord(studyId, recordId, data),
    );
  }

  /**
   * Delete QMS record
   */
  async deleteRecord(studyId: string, recordId: string): Promise<void> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }
    return this.qmsRepository.deleteQMSRecord(studyId, recordId);
  }

  /**
   * Close QMS record
   * Changes status to Closed
   */
  async closeRecord(
    studyId: string,
    recordId: string,
    note?: string,
  ): Promise<void> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }
    return this.qmsRepository.closeRecord(studyId, recordId, note);
  }

  /**
   * Reopen QMS record
   * Changes status back to Open
   */
  async reopenRecord(
    studyId: string,
    recordId: string,
    note?: string,
  ): Promise<void> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }
    return this.qmsRepository.reopenRecord(studyId, recordId, note);
  }

  /**
   * Add comment to QMS record
   * Validates comment text before adding
   */
  async addComment(
    studyId: string,
    recordId: string,
    text: string,
  ): Promise<QMSComment> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }
    if (!text?.trim()) {
      throw new Error("Comment text is required");
    }

    return this.qmsRepository.addComment(studyId, recordId, text);
  }

  async listComments(studyId: string, recordId: string): Promise<QMSComment[]> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }

    return this.qmsRepository.listComments(studyId, recordId);
  }

  async updateComment(
    studyId: string,
    recordId: string,
    commentId: string,
    text: string,
  ): Promise<QMSComment> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }
    if (!commentId) {
      throw new Error("Comment ID is required");
    }
    if (!text?.trim()) {
      throw new Error("Comment text is required");
    }

    return this.qmsRepository.updateComment(studyId, recordId, commentId, text);
  }

  async deleteComment(
    studyId: string,
    recordId: string,
    commentId: string,
  ): Promise<void> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }
    if (!commentId) {
      throw new Error("Comment ID is required");
    }

    return this.qmsRepository.deleteComment(studyId, recordId, commentId);
  }

  /**
   * List comments for a QMS record
   */
  async getComments(studyId: string, recordId: string): Promise<QMSComment[]> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }
    return this.qmsRepository.getComments(studyId, recordId);
  }

  /**
   * Add CAPA to QMS record
   * Validates CAPA data before adding
   */
  async addCapa(
    studyId: string,
    recordId: string,
    capa: CAPARecord,
  ): Promise<QMSRecord> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }

    const normalized = normalizeCapa(capa);
    validateCapaForSave(normalized, true);

    return this.enrichRecord(
      studyId,
      await this.qmsRepository.addCapa(studyId, recordId, normalized),
    );
  }

  /**
   * Update CAPA for QMS record
   * Validates CAPA data before updating
   */
  async updateCapa(
    studyId: string,
    recordId: string,
    capa: CAPARecord,
  ): Promise<QMSRecord> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }

    const normalized = normalizeCapa(capa);
    validateCapaForSave(normalized, false);

    return this.enrichRecord(
      studyId,
      await this.qmsRepository.updateCapa(studyId, recordId, normalized),
    );
  }

  /**
   * Approve CAPA effectiveness (QA/QC only)
   * Final approval of CAPA implementation
   */
  async approveCapa(
    studyId: string,
    recordId: string,
    payload: {
      effectivenessNotes?: string;
      eSignature: ESignatureCeremonyPayload;
    },
  ): Promise<QMSRecord> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    if (!recordId) {
      throw new Error("Record ID is required");
    }

    assertESignatureCeremonyPayload(payload.eSignature, "CAPA approval");

    return this.enrichRecord(
      studyId,
      await this.qmsRepository.approveCapa(studyId, recordId, payload),
    );
  }
}
